import { useNavigation } from '@react-navigation/native';
import { Box, HStack, Pressable, Spinner, Text } from 'native-base';
import React, { useMemo } from 'react';

import { ArrowDown } from 'app/components/icons';
import InfoPopover from 'app/components/InfoPopover';
import LineupAttractionInfo from 'app/components/ProductLineup/LineupAttractionInfo';
import useAttraction from 'app/hooks/useAttraction';
import { ProductContent } from 'app/hooks/useProduct';
import useReservationTextTranslationHelper from 'app/hooks/useReservationTextTranslationHelper';
import { useI18n } from 'app/providers/I18nProvider';
import Analytics, { Events } from 'app/services/Analytics';
import { hasSharedAdmissionOffers } from 'app/services/AttractionHelper';
import {
  AppAttractionGroup,
  AppLineupAttraction,
  AppLineupAttractionCard,
} from 'app/services/ProductLineupHelper';
import { isRedirect as $isRedirect } from 'app/services/ReservationHelper';
import { tID } from 'app/services/TestHelper';

export type LineupAttractionProps = {
  lineupAttraction: AppLineupAttraction;
  card: AppLineupAttractionCard;
  group: AppAttractionGroup;
  productContent: ProductContent;
};

export default function LineupAttraction({
  lineupAttraction,
  card,
  group,
  productContent,
}: LineupAttractionProps) {
  const { t } = useI18n();
  const navigation = useNavigation();
  const { attractionKey, contentKey, hasPastReservation, hasReservation, reservations } =
    lineupAttraction;
  const { attractionContent: attrContent, attractionConfig: attrConfig } = useAttraction({
    productContentKey: productContent.key ?? '',
    attractionContentKey: lineupAttraction.contentKey,
  });
  const rt = useReservationTextTranslationHelper({
    product: productContent,
    attraction: attrContent,
  });

  const isLoading = !attrContent || !attrConfig;
  const isClosed = attrConfig?.isClosed;
  const isRedirect = $isRedirect(attrConfig);
  const hasSharedAdmissionOffer = hasSharedAdmissionOffers(attrContent);
  const isDisabled = useMemo(() => {
    if (hasPastReservation) return true;
    if (hasReservation || hasSharedAdmissionOffer) return false;

    const splitTicketAttractionHasUsage = !hasReservation && card.hasReservation;
    if (splitTicketAttractionHasUsage) return true;

    const groupHasNoRemainingVisits =
      !hasReservation && group.remainingVisits === 0 && !hasSharedAdmissionOffer;
    if (groupHasNoRemainingVisits) return true;

    return false;
  }, [
    hasPastReservation,
    hasReservation,
    hasSharedAdmissionOffer,
    card.hasReservation,
    group.remainingVisits,
  ]);
  const showRedirectInfo =
    isRedirect && !isClosed && !isDisabled && !card.hasReservation && group.remainingVisits > 0;

  const handleNavigation = () => {
    Analytics.trackEvent(Events.TicketsSelectAttraction, {
      productKey: productContent.key ?? '',
      attractionKey: contentKey,
    });
    navigation.navigate('Tabs', {
      screen: 'AttractionTicketsStack',
      params: {
        screen: 'Attraction',
        params: {
          productId: productContent.key ?? '',
          attractionId: attrContent?.key ?? '',
        },
      },
    });
  };

  if (isLoading) {
    return <Spinner p="6" testID={tID(`LineupAttractionLoading-${attractionKey}`)} />;
  }

  return (
    <Pressable
      key={attrContent.key}
      _pressed={{
        bg: 'gray.600:alpha.10',
      }}
      opacity={isDisabled ? '.6' : '1'}
      p={4}
      testID={tID(`LineupAttraction-${attractionKey}`)}
      onPress={handleNavigation}
    >
      <HStack alignItems="center" justifyContent="space-between" space="1">
        <Box flexShrink="1">
          <Text bold fontSize="md" lineHeight="xs">
            {attrContent.name}
          </Text>

          <LineupAttractionInfo
            attrConfig={attrConfig}
            attrContent={attrContent}
            card={card}
            group={group}
            reservation={reservations?.[0]}
            rt={rt}
            t={t}
          />
        </Box>

        <HStack space="2">
          {showRedirectInfo && (
            <InfoPopover
              _icon={{ size: 'sm' }}
              _pressable={{ top: 0.5, testID: tID('lineup-attraction-popover-close') }}
              onOpen={() => {
                Analytics.trackEvent(Events.TicketsReservationInfo, {
                  productKey: productContent.key ?? '',
                  attractionKey: contentKey,
                });
              }}
            >
              <Text>{rt('redirect_info')}</Text>
            </InfoPopover>
          )}
          <ArrowDown style={{ transform: [{ rotate: `-90deg` }] }} />
        </HStack>
      </HStack>
    </Pressable>
  );
}
